import { Link } from 'react-router-dom';
import { ArrowRight, CircleDollarSign, ClipboardCheck, FileText, Search } from 'lucide-react';
import { usePlayers } from '@/hooks/usePlayers';

export function Endorsements() {
  const players = usePlayers({ status: 'active', sort: 'last_name' });
  const items = players.data?.items ?? [];
  const withNotes = items.filter((player) => Boolean(player.endorsementsText?.trim()));
  const needsSetup = items.filter((player) => !player.endorsementsText?.trim()).slice(0, 12);

  return (
    <section className="space-y-5">
      <div className="surface overflow-hidden rounded-control">
        <div className="flex flex-col gap-5 border-b border-border/80 p-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-bartlettGold">
              Commercial File Cabinet
            </p>
            <h1 className="mt-2 text-2xl font-semibold tracking-normal">Endorsements</h1>
            <p className="mt-1 max-w-2xl text-sm text-muted">
              Deal notes, payment timing, contract references, and deliverables organized by client.
            </p>
          </div>
          <div className="grid gap-2 sm:grid-cols-3 lg:min-w-[520px]">
            <Metric icon={CircleDollarSign} label="Tracked deals" value={String(withNotes.length)} />
            <Metric icon={ClipboardCheck} label="Needs setup" value={String(Math.max(items.length - withNotes.length, 0))} />
            <Metric icon={FileText} label="Contract vault" value="PDF ready" />
          </div>
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-[1fr_380px]">
        <div className="surface-soft rounded-control p-4">
          <div className="mb-3 flex items-baseline justify-between gap-2">
            <h2 className="text-base font-semibold">Deal board</h2>
            <span className="font-mono text-[11px] text-muted">{items.length} active clients</span>
          </div>
          {players.isLoading ? (
            <SkeletonRows />
          ) : withNotes.length > 0 ? (
            <div className="overflow-hidden rounded-control border border-border bg-white">
              {withNotes.map((player) => (
                <PlayerDealRow key={player.id} player={player} />
              ))}
            </div>
          ) : (
            <div className="rounded-control border border-dashed border-border bg-white px-4 py-8 text-center text-sm text-muted">
              No endorsement notes have been logged yet. Start from a player row and add the first deal record.
            </div>
          )}
        </div>

        <aside className="space-y-4">
          <div className="surface-soft rounded-control p-4">
            <h2 className="border-b border-border pb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted">
              Intake checklist
            </h2>
            <Checklist title="Deal terms" body="Brand, amount, payment timing, deliverables, and owner." />
            <Checklist title="Contract link" body="Signed PDF, W9/banking reference, renewal date, and obligations." />
            <Checklist title="Client questions" body="Fast answer surface for payments due, posts owed, and brand approvals." />
          </div>
          <div className="surface-soft rounded-control p-4">
            <div className="mb-3 flex items-center gap-2 text-muted">
              <Search size={14} />
              <h2 className="font-mono text-[11px] font-semibold uppercase tracking-[0.18em]">Needs setup</h2>
            </div>
            <div className="space-y-2">
              {needsSetup.map((player) => (
                <Link
                  key={player.id}
                  to={`/people/${player.id}?tab=endorsements`}
                  className="flex items-center justify-between gap-3 rounded-control border border-border bg-white px-3 py-2 text-sm hover:border-bartlett/40"
                >
                  <span className="truncate font-semibold">{player.displayName ?? `${player.firstName} ${player.lastName}`}</span>
                  <ArrowRight size={14} className="shrink-0 text-muted" />
                </Link>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </section>
  );
}

function PlayerDealRow({ player }: { player: { id: string; firstName: string; lastName: string; displayName: string | null; currentTeam: string | null; endorsementsText?: string | null } }) {
  return (
    <Link
      to={`/people/${player.id}?tab=endorsements`}
      className="grid gap-2 border-b border-border px-4 py-3 last:border-b-0 hover:bg-bg sm:grid-cols-[220px_1fr_auto]"
    >
      <div className="min-w-0">
        <div className="truncate text-sm font-semibold">{player.displayName ?? `${player.firstName} ${player.lastName}`}</div>
        <div className="mt-0.5 font-mono text-[11px] text-muted">{player.currentTeam ?? '—'}</div>
      </div>
      <p className="line-clamp-2 text-sm text-muted">{player.endorsementsText}</p>
      <span className="inline-flex items-center gap-1 font-mono text-[11px] uppercase text-bartlett">
        Open <ArrowRight size={12} />
      </span>
    </Link>
  );
}

function Metric({ icon: Icon, label, value }: { icon: typeof CircleDollarSign; label: string; value: string }) {
  return (
    <div className="rounded-control border border-border bg-bartlettIce px-3 py-2">
      <div className="flex items-center gap-1.5 text-muted">
        <Icon size={13} />
        <span className="font-mono text-[10px] uppercase tracking-[0.12em]">{label}</span>
      </div>
      <div className="mt-1 font-mono text-[18px] font-semibold text-ink">{value}</div>
    </div>
  );
}

function Checklist({ title, body }: { title: string; body: string }) {
  return (
    <div className="border-b border-border py-3 last:border-b-0">
      <div className="text-sm font-semibold">{title}</div>
      <p className="mt-1 text-[13px] leading-5 text-muted">{body}</p>
    </div>
  );
}

function SkeletonRows() {
  return (
    <div className="space-y-2">
      {[1, 2, 3, 4].map((item) => (
        <div key={item} className="h-16 animate-pulse rounded-control border border-border bg-white" />
      ))}
    </div>
  );
}
